import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Copy, IndentIncrease, RotateCcw, Star } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import {
  DurationIcon,
  MotivationIcon,
  RecurringIcon,
  TagIcon,
  TaskAttentionIcon,
  TaskDeadlineIcon,
  TaskStartIcon,
} from '@/src/renderer/components/icons'
import { DormantIcon } from '@/src/renderer/components/icons/DormantIcon'
import { ReactivateIcon } from '@/src/renderer/components/icons/ReactivateIcon'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/src/renderer/components/ui/dropdown-menu'
import type { ButtonShortcut } from '@/src/renderer/components/ui/Button/Button.types'
import type { LifeSpace, Project } from '@/src/renderer/lib/db/models'
import { useShortcutText } from '@/src/renderer/hooks/useShortcutText'
import type { TaskEntryData } from '../TaskEntry'
import type { TaskMenuHandlers } from '../TaskMenu/TaskMenu.types'
import { TaskMenuShortcutBox } from '../TaskMenu/TaskMenuShortcutBox'
import { TaskMenuTargetContent } from '../TaskMenu/TaskMenuTargetContent'
import { MenuAction, MenuSub } from '../TaskMenu/TaskMenuRows'
import { MenuKitContext, dropdownMenuKit } from '../TaskMenu/menuKit'
import { TASK_MENU_SHORTCUTS } from '../TaskMenu/taskMenuShortcuts'
import { MoveShortcutBox } from '../MoveMenu'
import { NestPicker, type NestPickerGroup } from '../NestPicker'
import type { MoveTarget } from '../../utils/moveTasks'
import type { RemovableMarkers } from '../../utils/removableMarkers'
import {
  isSelectionPicker,
  type SelectionCommand,
  type SelectionCommandId,
  type SelectionLayout,
} from '../../utils/selectionCommands'
import {
  SelectionCompleteIcon,
  SelectionDeleteIcon,
  SelectionMoreIcon,
  SelectionMoveIcon,
  SelectionUnfavoriteIcon,
  type SelectionCommandIconProps,
} from './icons'
import './SelectionActions.scss'

/** Verschieben, wie es die Seite anbietet (`MoveMenuContext`) — schon auf die passenden Aufgaben gebunden. */
export interface SelectionMove {
  startSpace: LifeSpace
  countMoving: () => Promise<number>
  onSelect: (target: MoveTarget, place: string, project: Project) => void
  onCreateProject: (project: { name: string; lifeSpace: LifeSpace }) => void
}

/** Einhängen, wie es die Seite anbietet (`NestMenuContext`) — schon auf die passenden Aufgaben gebunden. */
export interface SelectionNest {
  groups: () => NestPickerGroup[]
  onSelect: (parentId: string) => void
}

export interface SelectionActionsProps {
  /** Wie viele Aufgaben markiert sind. */
  count: number
  /** Was in der Pille steht und was im ⋯ (`layoutSelectionCommands`). */
  layout: SelectionLayout
  /** Die Aufgabe, deren Werte die Kästen zeigen — die oberste der offenen. */
  task: TaskEntryData
  /** Die Picker, gebunden an die Aufgaben, für die sie gelten. */
  handlers: TaskMenuHandlers
  /** Entfernen zeigt ein Kasten, sobald IRGENDEINE dieser Aufgaben die Marke trägt (#313, `removableMarkers`). */
  removable?: RemovableMarkers
  /** Befehle ohne Kasten: Erledigen, Ruhen, Favorit, Duplizieren, Löschen. */
  onCommand: (id: SelectionCommandId) => void
  move?: SelectionMove
  nest?: SelectionNest
  /** „Ruhen lassen“ im Start-Kasten — fehlt, wenn keine der Aufgaben wach und offen ist. */
  onRest?: () => void
  /** Ein Befehl ist durch oder ein Kasten zu: Die Tastatur gehört wieder der Liste (`restoreListFocus`). */
  onDone?: () => void
}

type Box = 'start' | 'move' | 'more'

/** Eine Spur kleiner als die Zeichen der Nachbarn (24px, Sven 08.10.2026); der Knopf bleibt 32px (SelectionActions.scss). */
const ICON_SIZE = 20

/**
 * Das Zeichen je Befehl, wie im Aufgaben-Menü (`TaskMenu`); Zwei-Stellungs-Befehle nach ihrer Stellung.
 * Wo sich beim Überfahren Teile bewegen (#315), steht ein Nachbau von lucide mit Teil-Klassen.
 */
function CommandIcon({ command, size }: { command: SelectionCommand; size: number }) {
  // In der Pille setzt SelectionActions.scss den Strich (1,5px); im ⋯ gilt der des Aufgaben-Menüs.
  const lucide = (Icon: LucideIcon | React.FC<SelectionCommandIconProps>) => <Icon size={size} strokeWidth={2} />
  switch (command.id) {
    case 'complete': return command.mode === 'reopen' ? lucide(RotateCcw) : lucide(SelectionCompleteIcon)
    case 'start': return <TaskStartIcon size={size} />
    case 'deadline': return <TaskDeadlineIcon size={size} />
    case 'duration': return <DurationIcon size={size} />
    case 'recurrence': return <RecurringIcon size={size} />
    case 'attention': return <TaskAttentionIcon size={size} />
    case 'tags': return <TagIcon size={size} />
    case 'reward': return <MotivationIcon size={size} />
    case 'rest': return command.mode === 'wake' ? <ReactivateIcon size={size} /> : <DormantIcon size={size} />
    case 'favorite': return command.mode === 'remove' ? lucide(SelectionUnfavoriteIcon) : lucide(Star)
    case 'duplicate': return lucide(Copy)
    case 'move': return lucide(SelectionMoveIcon)
    case 'nest': return lucide(IndentIncrease)
    case 'delete': return lucide(SelectionDeleteIcon)
  }
}

/** Das Kürzel im Tooltip der Pille — dieselben Tasten wie im Aufgaben-Menü (`taskMenuShortcuts.ts`). */
const TOOLTIP_SHORTCUTS: Partial<Record<SelectionCommandId, ButtonShortcut>> = {
  start: { kind: 'character', text: 'S' },
  deadline: { kind: 'character', text: 'E' },
  duration: { kind: 'character', text: 'D' },
  recurrence: { kind: 'character', text: 'R', mod: true, shift: true },
  attention: { kind: 'character', text: 'A', mod: true, shift: true },
  tags: { kind: 'character', text: 'T' },
  reward: { kind: 'character', text: 'G', mod: true, shift: true },
  rest: { kind: 'character', text: 'L', mod: true, shift: true },
  favorite: { kind: 'character', text: 'F', mod: true, shift: true },
  duplicate: { kind: 'character', text: 'D', mod: true },
  move: { kind: 'character', text: 'M', mod: true, shift: true },
  delete: { kind: 'character', text: '⌫' },
}

/** Die Kürzel im ⋯ — wie im Aufgaben-Menü; Erledigen steht ohne (wie im Menü „Aufgaben“). */
const MENU_SHORTCUTS: Partial<Record<SelectionCommandId, string>> = {
  start: TASK_MENU_SHORTCUTS.start,
  deadline: TASK_MENU_SHORTCUTS.deadline,
  duration: TASK_MENU_SHORTCUTS.duration,
  recurrence: TASK_MENU_SHORTCUTS.recurrence,
  attention: TASK_MENU_SHORTCUTS.attention,
  tags: TASK_MENU_SHORTCUTS.tags,
  reward: TASK_MENU_SHORTCUTS.reward,
  rest: TASK_MENU_SHORTCUTS.rest,
  favorite: TASK_MENU_SHORTCUTS.favorite,
  duplicate: TASK_MENU_SHORTCUTS.duplicate,
  move: TASK_MENU_SHORTCUTS.move,
  nest: TASK_MENU_SHORTCUTS.nest,
  delete: TASK_MENU_SHORTCUTS.delete,
}

/** Der Tooltip in der Pille ohne die drei Punkte — „Start (S)“, nicht „Start … (S)“. */
const withoutEllipsis = (label: string) => label.replace(/\s*…$/, '')

/**
 * **Die Befehle der Auswahl in der Listen-Werkzeug-Pille** (#291, CONTEXT.md): Anzahl, bis zu drei
 * Knöpfe und das ⋯ als Überlauf. Was für keine der markierten Aufgaben geht, fehlt; was für einige
 * geht, wirkt nur auf die — der Start- und der Verschieben-Kasten sagen es in einer Zeile, das ⋯
 * nicht. Die Kästen sind die vorhandenen (Taste S, ⇧⌘M), nur am Knopf statt an der Zeile.
 *
 * Nur die Bedienung: Was gilt und wohin geschrieben wird, rechnet die Seite (`useSelectionActions`).
 *
 * @example
 * <SelectionActions count={3} layout={layout} task={anchor} handlers={handlers}
 *   onCommand={run} move={move} nest={nest} onRest={rest} />
 */
export const SelectionActions: React.FC<SelectionActionsProps> = ({
  count, layout, task, handlers, removable, onCommand, move, nest, onRest, onDone,
}) => {
  const { t } = useTranslation()
  const shortcutText = useShortcutText()
  // `armed`: Überfahren wechselt — an, sobald ein Kasten aufging, und über Löschen & Co. hinweg (siehe `hoverTo`).
  const [menu, setMenu] = useState<{ box: Box | null; armed: boolean }>({ box: null, armed: false })
  const box = menu.box
  const setBox = (next: Box | null) => setMenu({ box: next, armed: true })
  const disarm = () => setMenu({ box: null, armed: false })
  // Beim Wechsel per Überfahren steht der nächste Kasten sofort da, ohne Einblenden.
  const [instant, setInstant] = useState(false)
  const [openSub, setOpenSub] = useState<SelectionCommandId | null>(null)
  /*
    **Danach gehört die Tastatur wieder der Liste** (gemeldet 08.10.2026): Behielte ein Knopf der Pille den Fokus,
    gingen ⌫, Leertaste und Pfeile ins Leere — die Liste hört nur, solange sie ihn hat. `restoreListFocus` holt
    ihn nur aus dem Nichts (`body`) zurück; deshalb gibt die Pille ihn zuerst ab.
  */
  const finish = () => {
    const active = document.activeElement
    if (active instanceof HTMLElement && active.closest('.selection-actions')) active.blur()
    onDone?.()
  }
  const close = () => { disarm(); setOpenSub(null); finish() }

  const labelOf = (command: SelectionCommand): string => {
    switch (command.id) {
      case 'complete': return t(command.mode === 'reopen' ? 'taskItem.menu.reopen' : 'menu.tasks.complete')
      case 'start': return t('taskItem.menu.when')
      case 'rest': return t(command.mode === 'wake' ? 'taskItem.menu.wakeNow' : 'taskItem.menu.rest')
      case 'favorite': return t(command.mode === 'remove' ? 'favorites.remove' : 'favorites.add')
      case 'nest': return t('taskList.nest.menu')
      default: return t(`taskItem.menu.${command.id}`)
    }
  }

  // Die Zeile im Kasten, wenn er nicht alle meint (Sven, 08.10.2026) — der Rest sind die erledigten.
  const noticeFor = (id: SelectionCommandId): string | undefined => {
    const command = [...layout.pill, ...layout.overflow].find(entry => entry.id === id)
    if (!command || command.fits.length >= count) return undefined
    return t('listToolbar.partialDone', { count: count - command.fits.length, fits: command.fits.length, total: count })
  }

  // Liegt Verschieben im ⋯, öffnet das Panel an einem Punkt neben dem ⋯ — wie aus dem Rechtsklick.
  const moveInOverflow = layout.overflow.some(command => command.id === 'move')
  /*
    **Offen heißt hervorgehoben** (Sven, 08.10.2026): Solange ein Kasten oder das ⋯-Menü offen steht, zeigt
    sein Knopf es im Hover-Ton von „Neue Aufgabe“. Das Verschieben-Panel aus dem ⋯ hängt am ⋯ — dann ist es das ⋯.
  */
  const openButton: SelectionCommandId | 'more' | null =
    box === 'start' ? 'start'
      : box === 'move' ? (moveInOverflow ? 'more' : 'move')
        : box === 'more' ? 'more'
          : null
  const buttonClass = (id: SelectionCommandId | 'more') =>
    `list-toolbar__button selection-actions__button${openButton === id ? ' selection-actions__button--open' : ''}`

  const button = (command: SelectionCommand, props: React.ButtonHTMLAttributes<HTMLButtonElement> = {}) => (
    <WithTooltip
      side="top"
      sideOffset={16}
      content={withoutEllipsis(labelOf(command))}
      shortcut={TOOLTIP_SHORTCUTS[command.id]}
    >
      <button
        type="button"
        aria-label={labelOf(command)}
        className={buttonClass(command.id)}
        aria-expanded={command.id === 'start' || command.id === 'move' ? openButton === command.id : undefined}
        data-command={command.id}
        // Die Stellung wählt die Bewegung beim Überfahren (#315): Wieder öffnen dreht, Erledigen zeichnet.
        data-mode={command.mode}
        // Der Tausch in der Pille (#291) blendet die Teile der Auswahl einzeln ein — in dieser Reihenfolge.
        data-swap-item="selection"
        {...props}
      >
        <CommandIcon command={command} size={ICON_SIZE} />
      </button>
    </WithTooltip>
  )

  const openBox = (next: Box) => (open: boolean) => {
    if (open) {
      setInstant(false)
      setBox(next)
      return
    }
    // Ein Schließen gilt nur dem, der noch offen ist: Beim Wechsel per Überfahren meldet der alte Kasten sein
    // Zugehen erst, wenn der neue schon steht.
    setMenu(current => (current.box === next ? { box: null, armed: false } : current))
    if (box === next) finish()
  }

  /*
    **Überfahren wechselt** (Sven, 08.10.2026) — wie eine Menüleiste: Steht ein Kasten oder das ⋯ offen,
    öffnet das Überfahren eines anderen Knopfs dessen Kasten; ein Knopf ohne Kasten (Löschen & Co.) schließt
    den offenen, damit er gleich zu drücken ist — das Wechseln bleibt aber an: Zurück auf Start geht dessen
    Kasten wieder auf. Es endet mit einem Klick, Esc, einer Wahl oder wenn der Zeiger die Pille ohne offenen
    Kasten verlässt. Davor tut Überfahren nichts.
  */
  const hoverTo = (target: Box | null) => () => {
    if (!menu.armed || box === target) return
    setInstant(true)
    setOpenSub(null)
    setBox(target)
  }

  const startBox = (trigger: React.ReactElement) => (
    <TaskMenuShortcutBox
      key="start"
      target="start"
      task={task}
      handlers={handlers}
      onRest={onRest}
      removable={removable}
      onClose={close}
      anchor="trigger"
      trigger={trigger}
      open={box === 'start'}
      onOpenChange={openBox('start')}
      notice={noticeFor('start')}
      sheetTitle={t('listToolbar.selectedCount', { count })}
      instant={instant}
    />
  )

  const moveBox = (trigger: React.ReactElement) => move && (
    <MoveShortcutBox
      key="move"
      anchor="trigger"
      trigger={trigger}
      open={box === 'move'}
      onOpenChange={openBox('move')}
      notice={noticeFor('move')}
      instant={instant}
      taskTitle={task.title || t('taskList.nest.untitled')}
      countMoving={move.countMoving}
      startSpace={move.startSpace}
      current={null}
      onSelect={move.onSelect}
      onCreateProject={move.onCreateProject}
      onClose={close}
    />
  )

  const pillButton = (command: SelectionCommand) => {
    if (command.id === 'start') return startBox(button(command, { onPointerEnter: hoverTo('start') }))
    if (command.id === 'move') return moveBox(button(command, { onPointerEnter: hoverTo('move') }))
    return (
      <React.Fragment key={command.id}>
        {button(command, { onClick: () => { disarm(); onCommand(command.id); finish() }, onPointerEnter: hoverTo(null) })}
      </React.Fragment>
    )
  }

  const menuRow = (command: SelectionCommand) => {
    const label = labelOf(command)
    const icon = <CommandIcon command={command} size={16} />
    const shortcut = MENU_SHORTCUTS[command.id] ? shortcutText(MENU_SHORTCUTS[command.id]!) : undefined
    if (isSelectionPicker(command.id)) {
      return (
        <MenuSub
          key={command.id}
          icon={icon}
          label={label}
          shortcut={shortcut}
          marked={false}
          open={openSub === command.id}
          onOpenChange={(next) => setOpenSub(next ? command.id : null)}
        >
          <TaskMenuTargetContent target={command.id} task={task} handlers={handlers} onRest={onRest} removable={removable} onClose={close} />
        </MenuSub>
      )
    }
    if (command.id === 'nest' && nest) {
      return (
        <MenuSub
          key={command.id}
          icon={icon}
          label={label}
          shortcut={shortcut}
          marked={false}
          open={openSub === 'nest'}
          onOpenChange={(next) => setOpenSub(next ? 'nest' : null)}
        >
          <NestPicker
            groups={openSub === 'nest' ? nest.groups() : []}
            onSelect={(parentId) => { close(); nest.onSelect(parentId) }}
            onClose={close}
          />
        </MenuSub>
      )
    }
    return (
      <MenuAction
        key={command.id}
        danger={command.id === 'delete'}
        icon={icon}
        label={label}
        shortcut={shortcut}
        onSelect={() => {
          if (command.id === 'move') {
            // Erst zu, dann auf: Das Menü gibt den Fokus zurück, bevor das Panel ihn nimmt.
            setBox(null)
            requestAnimationFrame(() => setBox('move'))
            return
          }
          onCommand(command.id)
        }}
      />
    )
  }

  // Gruppen wie im Aufgaben-Menü, durch Striche getrennt.
  const groups: SelectionCommand[][] = []
  for (const command of layout.overflow) {
    const last = groups[groups.length - 1]
    if (last && last[0]!.group === command.group) last.push(command)
    else groups.push([command])
  }

  return (
    <div
      className="list-toolbar__group list-toolbar__group--selection selection-actions"
      role="group"
      aria-label={t('listToolbar.selectionLabel')}
      onPointerLeave={() => setMenu(current => (current.box === null ? { box: null, armed: false } : current))}
    >
      <span className="selection-actions__count" data-swap-item="selection" aria-live="polite" aria-label={t('listToolbar.selectedCount', { count })}>
        {count}
      </span>
      {layout.pill.map(pillButton)}
      {moveInOverflow && moveBox(<span className="selection-actions__anchor" aria-hidden="true" />)}
      {layout.overflow.length > 0 && (
        // Nicht modal: Die Nachbarn in der Pille bleiben erreichbar, Überfahren wechselt zu ihnen.
        <DropdownMenu open={box === 'more'} onOpenChange={openBox('more')} modal={false}>
          <DropdownMenuTrigger asChild>
            <WithTooltip side="top" sideOffset={16} content={t('listToolbar.more')}>
              <button
                type="button"
                aria-label={t('listToolbar.more')}
                className={buttonClass('more')}
                onPointerEnter={hoverTo('more')}
                data-command="more"
                data-swap-item="selection"
              >
                <SelectionMoreIcon size={ICON_SIZE} />
              </button>
            </WithTooltip>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="center"
            sideOffset={12}
            className="context-menu-content--space"
            // Nicht zurück an das ⋯, sondern an die Liste — sonst hörte sie keine Tasten mehr.
            onCloseAutoFocus={(event) => { event.preventDefault(); finish() }}
          >
            <MenuKitContext.Provider value={dropdownMenuKit}>
              {groups.map((group, i) => (
                <React.Fragment key={group[0]!.group}>
                  {i > 0 && <dropdownMenuKit.Separator />}
                  <dropdownMenuKit.Group>{group.map(menuRow)}</dropdownMenuKit.Group>
                </React.Fragment>
              ))}
            </MenuKitContext.Provider>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
